import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { PencilSimple } from "@phosphor-icons/react";

const FIELDS = {
  profile: [
    ["first_name", "First name"], ["last_name", "Last name"], ["email", "Email ID", "email"], ["phone", "Mobile number"],
    ["date_of_birth", "Date of birth", "date"], ["gender", "Gender", "select", ["male", "female", "other"]],
  ],
  work: [
    ["department", "Department"], ["designation", "Designation"], ["grade", "Grade"], ["band", "Band"],
    ["level", "Level (1-15)", "number"], ["date_of_joining", "Date of joining", "date"],
    ["employment_type", "Employment type", "select", ["Full-time", "Part-time", "Contract", "Intern"]],
    ["location", "Branch / Location"], ["status", "Status", "select", ["active", "inactive", "exited"]],
  ],
  personal: [
    ["blood_group", "Blood group"], ["uan", "UAN (PF)"], ["emergency_contact_name", "Emergency contact name"],
    ["emergency_contact_relation", "Relation"], ["emergency_contact_phone", "Emergency contact phone"],
    ["permanent_address", "Permanent address"], ["current_address", "Current address"],
  ],
};
const ALL_KEYS = Object.values(FIELDS).flat().map((f) => f[0]).concat(["reporting_manager_id", "dotted_manager_id", "pt_state"]);

export default function EmployeeEditDialog({ emp, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [managers, setManagers] = useState([]);
  const [states, setStates] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const f = {}; ALL_KEYS.forEach((k) => { f[k] = emp[k] ?? ""; }); setForm(f);
    api.get("/employees", { params: emp.tenant_id ? { tenant_id: emp.tenant_id } : {} }).then(({ data }) => setManagers(data.filter((e) => e.id !== emp.id))).catch(() => {});
    api.get("/salary-templates/pt-states").then(({ data }) => setStates(data)).catch(() => {});
  }, [open, emp]);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      ALL_KEYS.forEach((k) => { const v = form[k]; if (v !== "" && v !== null && v !== undefined && v !== emp[k]) patch[k] = k === "level" ? parseInt(v) : v; });
      if (form.reporting_manager_id === "__none") patch.reporting_manager_id = null;
      await api.patch(`/employees/${emp.id}`, patch);
      toast.success("Employee updated"); setOpen(false); onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  const render = ([k, label, type, opts]) => (
    <div key={k} className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {type === "select" ? (
        <Select value={form[k] || ""} onValueChange={(v) => setForm({ ...form, [k]: v })}>
          <SelectTrigger className="rounded-sm" data-testid={`edit-${k}`}><SelectValue placeholder="—" /></SelectTrigger>
          <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      ) : (
        <Input type={type || "text"} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="rounded-sm" data-testid={`edit-${k}`} />
      )}
    </div>
  );
  const mgrSelect = (k, label) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Select value={form[k] || ""} onValueChange={(v) => setForm({ ...form, [k]: v })}>
        <SelectTrigger className="rounded-sm" data-testid={`edit-${k}`}><SelectValue placeholder="Not set" /></SelectTrigger>
        <SelectContent>{managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.first_name} {m.last_name} · {m.employee_code}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );

  return (
    <>
      <Button onClick={() => setOpen(true)} variant="outline" className="rounded-sm" data-testid="emp-edit-btn"><PencilSimple size={14} className="mr-1" /> Edit details</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="emp-edit-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Edit · {emp.first_name} {emp.last_name}</DialogTitle></DialogHeader>
          <Tabs defaultValue="profile">
            <TabsList className="rounded-sm">
              <TabsTrigger value="profile" className="rounded-sm" data-testid="edit-tab-profile">Profile</TabsTrigger>
              <TabsTrigger value="work" className="rounded-sm" data-testid="edit-tab-work">Work</TabsTrigger>
              <TabsTrigger value="hierarchy" className="rounded-sm" data-testid="edit-tab-hierarchy">Hierarchy</TabsTrigger>
              <TabsTrigger value="personal" className="rounded-sm" data-testid="edit-tab-personal">Personal</TabsTrigger>
            </TabsList>
            <TabsContent value="profile" className="grid grid-cols-2 gap-3 mt-3">{FIELDS.profile.map(render)}</TabsContent>
            <TabsContent value="work" className="grid grid-cols-2 gap-3 mt-3">
              {FIELDS.work.map(render)}
              <div className="space-y-1">
                <Label className="text-xs">Professional-tax state</Label>
                <Select value={form.pt_state || ""} onValueChange={(v) => setForm({ ...form, pt_state: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="edit-pt_state"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent>{states.map((s) => <SelectItem key={s.state} value={s.state}>{s.state}{s.has_pt ? "" : " (no PT)"}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </TabsContent>
            <TabsContent value="hierarchy" className="grid grid-cols-1 gap-3 mt-3">
              {mgrSelect("reporting_manager_id", "Reporting manager (solid line)")}
              {mgrSelect("dotted_manager_id", "Dotted-line manager (optional)")}
              <div className="text-[11px] text-zinc-500">Managers see everyone mapped under them in their Team view and Approvals inbox.</div>
            </TabsContent>
            <TabsContent value="personal" className="grid grid-cols-2 gap-3 mt-3">{FIELDS.personal.map(render)}</TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} disabled={saving} className="btn-zenith rounded-sm" data-testid="emp-edit-save">{saving ? "Saving…" : "Save changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
